import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { calculateInvoiceTotals } from '../utils/helpers';
import Alert from '../components/Alert';
import InvoiceItemsTable from '../components/InvoiceItemsTable';
import CustomerModal from '../components/CustomerModal';
import Layout from '../components/Layout';
import AutocompleteSelect from '../components/AutocompleteSelect';
import CustomDropdown from '../components/CustomDropdown';
import {

  ArrowLeft,


  Package,
  Calculator,
  FileText,

  Loader2,


  Phone,
  Mail,
  NotepadText,
  User,
  UserPlus,
  Plus
} from 'lucide-react';
import './CreateInvoice.css';
import './CreateQuotation.css';

const CreateQuotation = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settings, setSettings] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [showCustomerModal, setShowCustomerModal] = useState(false);

    // Calculate default valid until date (30 days from now)
    const getDefaultValidUntil = () => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        return date.toISOString().split('T')[0];
    };

    const [formData, setFormData] = useState({
        customer: '',
        quotationDate: new Date().toISOString().split('T')[0],
        validUntil: getDefaultValidUntil(),
        items: [{
            product: '',
            description: '',
            hsnSac: '',
            quantity: 1,
            unit: 'pcs',
            rate: 0,
            gstRate: 18
        }],
        discount: 0,
        discountType: 'amount',
        taxType: 'excluded',
        notes: '',
        termsAndConditions: ''
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const [settingsRes, customersRes, productsRes] = await Promise.all([
                api.get('/settings'),
                api.get('/customers?limit=1000'),
                api.get('/products?limit=1000')
            ]);

            setSettings(settingsRes.data.settings);
            setCustomers(customersRes.data.customers);
            setProducts(productsRes.data.products);

            if (settingsRes.data.settings) {
                setFormData(prev => ({
                    ...prev,
                    taxType: settingsRes.data.settings.defaultTaxType || 'excluded',
                    termsAndConditions: settingsRes.data.settings.termsAndConditions || prev.termsAndConditions
                }));
            }
        } catch (err) {
            setError('Failed to load initial data');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCustomerCreate = async (customerData) => {
        try {
            const response = await api.post('/customers', customerData);
            const newCustomer = response.data.customer;

            setCustomers(prev => [...prev, newCustomer]);
            setFormData(prev => ({ ...prev, customer: newCustomer._id }));
            setSuccess('Customer added successfully!');
            setShowCustomerModal(false);

            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create customer');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.customer) {
            setError('Please select a customer');
            return;
        }

        if (formData.items.length === 0) {
            setError('Please add at least one item');
            return;
        }

        const hasEmptyItems = formData.items.some(
            item => (!item.productName && !item.description) || !item.hsnSac || Number(item.quantity) <= 0 || Number(item.rate) < 0
        );
        if (hasEmptyItems) {
            setError('Please fill in all item details (product name/description, HSN/SAC, quantity, and rate)');
            return;
        }

        try {
            setLoading(true);
            setError('');

            const sanitizedData = {
                ...formData,
                items: formData.items.map(item => {
                    const sanitizedItem = { ...item };
                    sanitizedItem.description = (sanitizedItem.description || sanitizedItem.productName || '').trim();
                    sanitizedItem.productName = (sanitizedItem.productName || '').trim();
                    sanitizedItem.unit = (sanitizedItem.unit || 'pcs').trim();
                    if (!sanitizedItem.product) {
                        delete sanitizedItem.product;
                    }
                    return sanitizedItem;
                })
            };

            const response = await api.post('/quotations', sanitizedData);

            if (response.data.success) {
                setSuccess('Quotation created successfully! Redirecting...');
                setTimeout(() => {
                    navigate(`/quotations/${response.data.quotation._id}`);
                }, 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create quotation');
            setLoading(false);
        }
    };

    const selectedCustomer = customers.find(c => c._id === formData.customer);
    const isIGST = settings && selectedCustomer ? selectedCustomer.placeOfSupply !== settings.state : false;
    const totals = calculateInvoiceTotals(formData.items, isIGST, formData.discount, formData.discountType, formData.taxType);

    return (
        <div>
            <div className="create-invoice-container">
                {/* Page Header */}
                <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '5px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <NotepadText size={32} style={{ color: '#06b6d4' }} /> Create Quotation
                            </h1>
                            <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                                Create a new quotation for your customer
                            </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/quotations')}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <ArrowLeft size={18} /> Back to Quotations
                    </button>
                </div>

                {error && <Alert type="error" message={error} onClose={() => setError('')} />}
                {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

                <form onSubmit={handleSubmit}>
                    {/* Customer Selection Card */}
                    <div className="card card-customer" style={{ marginBottom: '25px', position: 'relative', zIndex: 10, overflow: 'visible' }}>
                        <div style={{
                            padding: '20px',
                            borderBottom: '1px solid #e5e7eb',
                            background: '#f9fafb'
                        }}>
                            <h2 style={{
                                fontSize: '17px',
                                fontWeight: '600',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <User size={18} style={{ color: '#06b6d4' }} /> Customer Information
                            </h2>
                        </div>

                        <div style={{ padding: '25px' }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto 150px 150px 150px',
                                gap: '20px',
                                alignItems: 'flex-start'
                            }}>
                                <div>
                                    <label className="form-label" style={{ fontWeight: '500' }}>
                                        Select Customer <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <AutocompleteSelect
                                        options={customers}
                                        value={formData.customer}
                                        onChange={(value) => setFormData(prev => ({ ...prev, customer: value }))}
                                        displayField="name"
                                        valueField="_id"
                                        placeholder="Type to search customer by name, phone, or Product ID..."
                                        searchFields={['name', 'phone', 'email']}
                                        className="form-control"
                                        required
                                        renderOption={(customer) => (
                                            <div>
                                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#111' }}>
                                                    {customer.name}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Phone size={12} /> {customer.phone} {customer.email && <><Mail size={12} style={{ marginLeft: '4px' }} /> {customer.email}</>}
                                                </div>
                                            </div>
                                        )}
                                    />
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={() => setShowCustomerModal(true)}
                                    style={{
                                        marginTop: '28px',
                                        padding: '10px 18px',
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <UserPlus size={18} /> New Customer
                                </button>



                                <div>
                                    <label className="form-label" style={{ fontWeight: '500' }}>
                                        Quotation Date <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="quotationDate"
                                        value={formData.quotationDate}
                                        onChange={handleChange}
                                        className="form-control"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="form-label" style={{ fontWeight: '500' }}>
                                        Valid Until <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="validUntil"
                                        value={formData.validUntil}
                                        onChange={handleChange}
                                        className="form-control"
                                        required
                                        min={formData.quotationDate}
                                    />
                                </div>
                            </div>

                            {selectedCustomer && (
                                <div style={{
                                    marginTop: '20px',
                                    padding: '15px',
                                    background: '#ecfeff',
                                    borderLeft: '4px solid #06b6d4',
                                    borderRadius: '6px'
                                }}>
                                    <div style={{ fontSize: '14px' }}>
                                        <strong style={{ color: '#155e75' }}>Selected Customer:</strong>
                                    </div>
                                    <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', fontSize: '13px' }}>
                                        <div>
                                            <strong>Name:</strong> {selectedCustomer.name}
                                        </div>
                                        <div>
                                            <strong>Phone:</strong> {selectedCustomer.phone}
                                        </div>
                                        {selectedCustomer.email && (
                                            <div>
                                                <strong>Email:</strong> {selectedCustomer.email}
                                            </div>
                                        )}
                                        {selectedCustomer.city && (
                                            <div>
                                                <strong>City:</strong> {selectedCustomer.city}
                                            </div>
                                        )}
                                        {selectedCustomer.gstNumber && (
                                            <div>
                                                <strong>GST:</strong> {selectedCustomer.gstNumber}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quotation Items Card */}
                    <div className="card" style={{ marginBottom: '25px', overflow: 'visible', position: 'relative', zIndex: 9 }}>
                        <div style={{
                            padding: '20px',
                            borderBottom: '1px solid #e5e7eb',
                            background: '#f9fafb'
                        }}>
                            <h2 style={{
                                fontSize: '17px',
                                fontWeight: '600',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <Package size={18} style={{ color: '#06b6d4' }} /> Quotation Items
                            </h2>
                        </div>

                        <div style={{ padding: '25px' }}>
                            <InvoiceItemsTable
                                items={formData.items}
                                products={products}
                                onItemsChange={(items) => setFormData(prev => ({ ...prev, items }))}
                            />
                        </div>
                    </div>

                    {/* Notes & Totals Section */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginBottom: '25px' }}>
                        {/* Notes & Terms */}
                        <div className="card card-additional" style={{ overflow: 'visible', position: 'relative', zIndex: 8 }}>
                            <div style={{
                                padding: '20px',
                                borderBottom: '1px solid #e5e7eb',
                                background: '#f9fafb'
                            }}>
                                <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FileText size={18} style={{ color: '#06b6d4' }} /> Additional Information
                                </h2>
                            </div>

                            <div style={{ padding: '25px' }}>
                                <div style={{ marginBottom: '18px' }}>
                                    <label className="form-label">Notes (Optional)</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        className="form-control"
                                        rows="4"
                                        placeholder="Add special instructions or notes..."
                                    />
                                </div>

                                <div>
                                    <label className="form-label">Terms & Conditions (Optional)</label>
                                    <textarea
                                        name="termsAndConditions"
                                        value={formData.termsAndConditions}
                                        onChange={handleChange}
                                        className="form-control"
                                        rows="4"
                                        placeholder="Enter terms and conditions..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Totals & Discount */}
                        <div className="card card-totals" style={{ overflow: 'visible', position: 'relative', zIndex: 8 }}>
                            <div style={{
                                padding: '20px',
                                borderBottom: '1px solid #e5e7eb',
                                background: '#f9fafb'
                            }}>
                                <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Calculator size={18} style={{ color: '#06b6d4' }} /> Quotation Summary
                                </h2>
                            </div>

                            <div style={{ padding: '25px' }}>
                                {/* Discount Section */}
                                <div style={{ marginBottom: '25px', padding: '15px', background: '#fef3c7', borderRadius: '8px' }}>
                                    <label className="form-label" style={{ marginBottom: '12px', fontWeight: '600' }}>
                                        Apply Discount
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '10px' }}>
                                        <input
                                            type="number"
                                            name="discount"
                                            value={formData.discount}
                                            onChange={handleChange}
                                            className="form-control"
                                            min="0"
                                            step="0.01"
                                            placeholder="Enter discount"
                                        />
                                        <CustomDropdown
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleChange}
                      options={[
                        { value: 'amount', label: '₹ Amount' },
                        { value: 'percentage', label: '% Percent' }
                      ]}
                      className="form-control"
                      style={{ height: '42px', minWidth: '130px' }}
                    />
                                    </div>
                                </div>

                                {/* Totals Summary */}
                                <div style={{ fontSize: '15px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0' }}>
                                        <span style={{ color: '#666' }}>Subtotal</span>
                                        <span style={{ fontWeight: '600' }}>₹{totals.subtotal.toFixed(2)}</span>
                                    </div>

                                    {formData.discount > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0', color: '#dc2626' }}>
                                            <span>Discount</span>
                                            <span style={{ fontWeight: '600' }}>-₹{totals.discountAmount.toFixed(2)}</span>
                                        </div>
                                    )}

                                    {isIGST ? (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0' }}>
                                            <span style={{ color: '#666' }}>IGST</span>
                                            <span style={{ fontWeight: '600' }}>₹{totals.igst.toFixed(2)}</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0' }}>
                                                <span style={{ color: '#666' }}>CGST</span>
                                                <span style={{ fontWeight: '600' }}>₹{totals.cgst.toFixed(2)}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #e0e0e0' }}>
                                                <span style={{ color: '#666' }}>SGST</span>
                                                <span style={{ fontWeight: '600' }}>₹{totals.sgst.toFixed(2)}</span>
                                            </div>
                                        </>
                                    )}

                                    <div style={{ borderBottom: '2px solid #333', marginBottom: '15px', paddingBottom: '10px' }}></div>

                                    {/* Grand Total */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '18px 20px',
                                        background: 'var(--accent)',
                    borderRadius: '10px',
                    color: 'white',
                                        fontSize: '18px'
                                    }}>
                                        <span style={{ fontWeight: '700' }}>GRAND TOTAL</span>
                                        <span style={{ fontWeight: '700', fontSize: '22px' }}>₹{totals.grandTotal.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '15px',
                        justifyContent: 'flex-end',
                        padding: '20px 0'
                    }}>
                        <button
                            type="button"
                            onClick={() => navigate('/quotations')}
                            className="btn btn-secondary"
                            style={{ padding: '12px 35px', fontSize: '15px' }}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary"
                            style={{
                                padding: '12px 45px',
                                fontSize: '15px',
                                fontWeight: '600',
                                background: loading ? '#9ca3af' : 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                                border: 'none',
                                boxShadow: loading ? 'none' : '0 4px 15px rgba(6, 182, 212, 0.4)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}
                        >
                            {loading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                            {loading ? 'Creating Quotation...' : 'Create Quotation'}
                        </button>
                    </div>
                </form>

                {/* Customer Modal */}
                {showCustomerModal && (
                    <CustomerModal
                        isOpen={showCustomerModal}
                        onClose={() => setShowCustomerModal(false)}
                        onSubmit={handleCustomerCreate}
                    />
                )}
            </div>
        </div>
    );
};

export default CreateQuotation;
